import {
  ReorderModalConfirmation,
  CancelOrderModalConfirmation,
} from '@components/Order';
import React, { useState } from 'react';
import { IOrder, OrderStatus } from '@monofront/types';
import OrderBodyMobile from './OrderBodyMobile';
import OrderBodyDesktop from './OrderBodyDesktop';
import { OrderProductList } from '@screens/Order/OrderProductList';
import { getOrderStatus } from '@monofront/business/utils';
import {
  ABTEST_REORDER_PAGE,
  ORDER_CANCEL_TIME,
} from '@monofront/business/constants';
import { dataLayerTrackEvent } from '@utils/tracking';
import useI18n from '@hooks/i18n/useI18n';
import { trackEvent } from '@monofront/tracking';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { sendMessageToMobile } from '@utils/mobile';
import { useMediaQuery } from '@monofront/business/hooks';
import InformativeBox from '@monofront/design-system/components/InformativeBox/InformativeBox';
import Information from '@monofront/design-system/visuals/Icons/Information';
import { useTranslations } from 'next-intl';

interface IOrderBodyProps {
  order: IOrder;
}

export const OrderBody: React.FC<IOrderBodyProps> = ({ order }) => {
  const tAccount = useTranslations('Account');
  const isMobile = useMediaQuery(800);
  const [modalState, setModalState] = useState({
    displayCancel: false,
    displayReorder: false,
  });
  const [currentOrder, setCurrentOrder] = useState(order);
  const { locale } = useI18n();

  const isCancellable =
    new Date().getTime() - new Date(order.createdAt).getTime() <
      ORDER_CANCEL_TIME[locale] &&
    getOrderStatus(order) === OrderStatus.pending;

  const showModal = (type: 'cancel' | 'reorder') => {
    setModalState({
      displayCancel: type === 'cancel',
      displayReorder: type === 'reorder',
    });
  };

  const hideModals = () => {
    setModalState({
      displayCancel: false,
      displayReorder: false,
    });
  };

  const isABTestEnabled = useFeatureIsOn(ABTEST_REORDER_PAGE);

  const onReorderClick = () => {
    if (isABTestEnabled) {
      sendMessageToMobile('goToReorderPage', {
        orderId: currentOrder.id,
      });

      return;
    }
    showModal('reorder');

    trackEvent({
      type: 'LFReorderWidget',
      data: {
        action: 'open',
      },
    });
  };

  return (
    <>
      <div>
        {isMobile && (
          <OrderBodyMobile
            currentOrder={currentOrder}
            isCancellable={isCancellable}
            showCancelModal={() => showModal('cancel')}
            onReorder={onReorderClick}
          />
        )}
        {!isMobile && (
          <OrderBodyDesktop
            currentOrder={currentOrder}
            isCancellable={isCancellable}
            showCancelModal={() => showModal('cancel')}
            showReorderModal={() => {
              showModal('reorder');

              // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
              dataLayerTrackEvent('ReorderWidget', {
                action: 'reorderModalOpen',
              });

              trackEvent({
                type: 'LFReorderWidget',
                data: {
                  action: 'open',
                },
              });
            }}
          />
        )}
      </div>
      {currentOrder.lineItems.length > 0 && (
        <OrderProductList orderProducts={currentOrder.lineItems} />
      )}
      {currentOrder.lineItems.length === 0 && (
        <InformativeBox
          icon={<Information />}
          UNSAFE_className="tw-my-4"
          backgroundColor={'var(--clr-tertiary-2)'}
          hasImageOnMobile
        >
          <p>{tAccount('orderNoProduct')}</p>
        </InformativeBox>
      )}
      <ReorderModalConfirmation
        hide={hideModals}
        isOpen={modalState.displayReorder}
        order={currentOrder}
      />
      <CancelOrderModalConfirmation
        hide={hideModals}
        isOpen={modalState.displayCancel}
        order={currentOrder}
        onCancelSuccess={(order: IOrder) => {
          setCurrentOrder({ ...currentOrder, ...order });
        }}
      />
      <style jsx>{`
        .address-container,
        .tracking-container {
          display: flex;
          justify-content: space-between;
        }

        .address > div:not(:last-child) {
          align-items: flex-start;
        }

        .address {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-column-gap: 10px;
        }

        .address div {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .tracking {
          margin-right: var(--space-4);
        }

        .info {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          margin-bottom: var(--space-6);
        }

        .order {
          margin-top: var(--space-3);
          padding: var(--space-4) 0;
          border-top: var(--border);
          border-bottom: var(--border);
        }

        .mobile-only {
          display: none;
        }

        @media (max-width: 800px) {
          .mobile-only {
            display: block;
          }

          .desktop-only {
            display: none;
          }
        }
      `}</style>
    </>
  );
};
